import React, { useCallback, useEffect, useState } from "react";
import { Check, Mic } from "lucide-react";
import logger from "../../utils/logger";
import { subscribeToMediaDeviceChange } from "../../utils/mediaDeviceChange";

export interface AudioDevice {
  deviceId: string;
  label: string;
  groupId?: string;
}

interface MicPickerProps {
  audioId?: string;
  onSelect: (mic: AudioDevice) => void;
}

const MicSelector: React.FC<MicPickerProps> = ({ audioId, onSelect }) => {
  const [devices, setDevices] = useState<AudioDevice[]>([]);
  const [selectedId, setSelectedId] = useState<string>("");

  const isSystemAlias = (deviceId: string) =>
    deviceId === "default" || deviceId === "communications";

  const normalizeMicLabel = (label: string) =>
    label.replace(/^(Default|Communications)\s*-\s*/i, "").trim();

  const getMicKey = (mic: AudioDevice) =>
    mic.groupId || normalizeMicLabel(mic.label).toLowerCase() || mic.deviceId;

  const getUniqueMics = (mics: AudioDevice[]) => {
    const unique = new Map<string, AudioDevice>();

    mics.forEach((mic) => {
      const key = getMicKey(mic);
      const existing = unique.get(key);

      if (!existing) {
        unique.set(key, mic);
        return;
      }

      if (isSystemAlias(existing.deviceId) && !isSystemAlias(mic.deviceId)) {
        unique.set(key, mic);
      }
    });

    return Array.from(unique.values());
  };

  const loadMics = useCallback(
    async (requestPermission = false) => {
      try {
        if (requestPermission) {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
          });
          stream.getTracks().forEach((track) => track.stop());
        }

        const all = await navigator.mediaDevices.enumerateDevices();
        const mics = getUniqueMics(all
          .filter((device) => device.kind === "audioinput")
          .map((device) => ({
            deviceId: device.deviceId,
            label: device.label || "Microphone",
            groupId: device.groupId,
          })));

        setDevices(mics);
        setSelectedId((currentId) => {
          const next =
            mics.find((mic) => mic.deviceId === audioId) ||
            mics.find((mic) => mic.deviceId === currentId) ||
            mics[0];

          if (currentId && next && currentId !== next.deviceId) {
            onSelect?.(next);
          }

          return next?.deviceId || "";
        });
      } catch (err) {
        logger.error("❌ Microphone access error:", err);
      }
    },
    [audioId, onSelect],
  );

  /* ------------------ Load Mics ------------------ */
  useEffect(() => {
    loadMics(true);

    return subscribeToMediaDeviceChange(() => loadMics(false));
  }, [loadMics]);

  const selectMic = (mic: AudioDevice) => {
    if (mic.deviceId === selectedId) return;

    setSelectedId(mic.deviceId);
    onSelect?.(mic);
  };

  return (
    <div className="w-[420px] bg-gray-50 rounded-xl shadow-xl border py-2">
      {devices.map((mic, idx) => (
        <button
          key={mic.deviceId}
          onClick={() => selectMic(mic)}
          className={`flex items-start gap-3 w-full px-4 py-3 text-left
            hover:bg-gray-100 ${selectedId === mic.deviceId ? "bg-gray-100" : ""
            }`}
        >
          <div className="w-5 pt-1">
            {selectedId === mic.deviceId && (
              <Check className="w-5 h-5 text-blue-600" />
            )}
          </div>

          <div>
            <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
              <Mic className="w-4 h-4" />
              {mic.label}
            </div>
            {idx === 0 && (
              <div className="text-xs text-gray-500">System default</div>
            )}
          </div>
        </button>
      ))}
    </div>
  );
};

export default MicSelector;
